import "../types/i18next.d.ts";

import type { FireflyRuntime } from "@squide/firefly";
import { getI18nextPlugin, i18nextPlugin } from "@squide/i18next";
import { createInstance, type i18n } from "i18next";
import { useTranslation } from "react-i18next";
import type { I18nextCatalog, I18nextNamespace, I18nextResources } from "./i18nextResources.ts";
import type { I18nextTFunction } from "./i18nextTFunction.ts";

export { Trans } from "react-i18next";

const DefaultLanguage = "en";
const LazyLanguages = ["fr", "de", "es", "nl"] as const;

type LazyLanguage = (typeof LazyLanguages)[number];
type SupportedLanguage = typeof DefaultLanguage | LazyLanguage;

export type I18nextCatalogLoaders<TNamespace extends I18nextNamespace> = Record<
    LazyLanguage,
    () => Promise<I18nextCatalog<I18nextResources[TNamespace]>>
>;

function isLazyLanguage(language: string): language is LazyLanguage {
    return LazyLanguages.some(x => x === language);
}

export function createI18nextPlugin(runtime: FireflyRuntime) {
    const plugin = new i18nextPlugin<SupportedLanguage>(
        runtime,
        [DefaultLanguage, ...LazyLanguages],
        DefaultLanguage,
        "language",
        { detection: { order: [] } }
    );

    // With an empty detection order this always resolves to "en": the plugin is shared by every app in the host,
    // and detecting the browser language would switch apps that have no catalogs. Reviews will set its own language (S1).
    plugin.detectUserLanguage();

    return plugin;
}

/**
 * Creates a package's i18next instance, with the English catalog bundled and the other languages loaded on demand.
 * Components read it through `useI18nextTranslation`, so they render English without a runtime.
 */
export function createI18nextInstance<TNamespace extends I18nextNamespace>(
    namespace: TNamespace,
    en: I18nextResources[TNamespace],
    loaders: I18nextCatalogLoaders<TNamespace>
) {
    const instance = createInstance();

    void instance.init({
        lng: DefaultLanguage,
        fallbackLng: DefaultLanguage,
        ns: [namespace],
        defaultNS: namespace,
        resources: { [DefaultLanguage]: { [namespace]: en } },
        interpolation: { escapeValue: false },
        initAsync: false
    });

    const registeredRuntimes = new WeakSet<FireflyRuntime>();

    // Must run from a module's register function: the plugin refuses a lazy instance once the modules are registered.
    const register = (runtime: FireflyRuntime) => {
        if (registeredRuntimes.has(runtime)) {
            return;
        }

        registeredRuntimes.add(runtime);

        getI18nextPlugin(runtime).registerInstance(namespace, instance, {
            loadResources: async language => {
                if (!isLazyLanguage(language)) {
                    throw new Error(`[i18n] No "${language}" catalog to load.`);
                }

                return { [namespace]: await loaders[language]() };
            }
        });
    };

    // Same function as useI18nextTranslation's, narrowed so that a message with placeholders requires their values.
    return { instance, register, t: instance.t as unknown as I18nextTFunction<TNamespace> };
}

export function useI18nextTranslation<TNamespace extends I18nextNamespace>(namespace: TNamespace, instance: i18n) {
    const { t, i18n } = useTranslation(namespace, { i18n: instance });

    // Same function, narrowed so that a message with placeholders requires their values.
    return { t: t as unknown as I18nextTFunction<TNamespace>, i18n };
}
